
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface LabValue {
  name: string;
  normalRange: string;
  unit: string;
  description: string;
  gender?: 'male' | 'female' | 'both';
}

interface LabCategory {
  id: string;
  name: string;
  icon: string;
  values: LabValue[];
}

const labCategories: LabCategory[] = [
  {
    id: 'hematology',
    name: '血液一般',
    icon: 'ri-drop-line',
    values: [
      { name: '白血球数（WBC）', normalRange: '3,300～8,600', unit: '/μL', description: '感染症や炎症の指標', gender: 'both' },
      { name: '赤血球数（RBC）', normalRange: '男性：435～555万 / 女性：386～492万', unit: '/μL', description: '貧血の評価に使用', gender: 'both' },
      { name: 'ヘモグロビン（Hb）', normalRange: '男性：13.7～16.8 / 女性：11.6～14.8', unit: 'g/dL', description: '酸素運搬能力の指標', gender: 'both' },
      { name: 'ヘマトクリット（Ht）', normalRange: '男性：40.7～50.1 / 女性：35.1～44.4', unit: '%', description: '血液中の赤血球の割合', gender: 'both' },
      { name: '血小板数（PLT）', normalRange: '15.8～34.8万', unit: '/μL', description: '止血機能の評価', gender: 'both' },
    ]
  },
  {
    id: 'liver',
    name: '肝機能',
    icon: 'ri-heart-pulse-line',
    values: [
      { name: 'AST（GOT）', normalRange: '13～30', unit: 'U/L', description: '肝細胞障害の指標', gender: 'both' },
      { name: 'ALT（GPT）', normalRange: '男性：10～42 / 女性：7～23', unit: 'U/L', description: '肝臓特異性の高い酵素', gender: 'both' },
      { name: 'γ-GTP', normalRange: '男性：13～64 / 女性：9～32', unit: 'U/L', description: 'アルコール性肝障害の指標', gender: 'both' },
      { name: 'ALP', normalRange: '106～322', unit: 'U/L', description: '胆道系疾患の指標', gender: 'both' },
      { name: '総ビリルビン', normalRange: '0.4～1.5', unit: 'mg/dL', description: '黄疸の評価', gender: 'both' },
      { name: 'アルブミン', normalRange: '4.1～5.1', unit: 'g/dL', description: '肝臓の蛋白合成能の指標', gender: 'both' },
    ]
  },
  {
    id: 'kidney',
    name: '腎機能',
    icon: 'ri-flask-line',
    values: [
      { name: '血清クレアチニン（Scr）', normalRange: '男性：0.65～1.07 / 女性：0.46～0.79', unit: 'mg/dL', description: '腎機能の基本指標', gender: 'both' },
      { name: 'eGFR', normalRange: '60以上', unit: 'mL/min/1.73m²', description: '腎機能の総合評価', gender: 'both' },
      { name: '尿素窒素（BUN）', normalRange: '8～20', unit: 'mg/dL', description: '腎機能・蛋白代謝の指標', gender: 'both' },
      { name: '尿酸（UA）', normalRange: '男性：3.7～7.8 / 女性：2.6～5.5', unit: 'mg/dL', description: '痛風・高尿酸血症の指標', gender: 'both' },
    ]
  },
  {
    id: 'lipid',
    name: '脂質',
    icon: 'ri-heart-line',
    values: [
      { name: '総コレステロール（TC）', normalRange: '142～248', unit: 'mg/dL', description: '脂質代謝の総合指標', gender: 'both' },
      { name: 'LDLコレステロール', normalRange: '65～163', unit: 'mg/dL', description: '悪玉コレステロール', gender: 'both' },
      { name: 'HDLコレステロール', normalRange: '男性：38～90 / 女性：48～103', unit: 'mg/dL', description: '善玉コレステロール', gender: 'both' },
      { name: '中性脂肪（TG）', normalRange: '40～234', unit: 'mg/dL', description: '動脈硬化のリスク因子', gender: 'both' },
    ]
  },
  {
    id: 'glucose',
    name: '糖代謝',
    icon: 'ri-test-tube-line',
    values: [
      { name: '空腹時血糖（FPG）', normalRange: '73～109', unit: 'mg/dL', description: '糖尿病の診断基準', gender: 'both' },
      { name: 'HbA1c（NGSP）', normalRange: '4.9～6.0', unit: '%', description: '過去1～2ヶ月の血糖コントロール', gender: 'both' },
      { name: '随時血糖', normalRange: '140未満', unit: 'mg/dL', description: '食後の血糖値', gender: 'both' },
    ]
  },
  {
    id: 'electrolyte',
    name: '電解質',
    icon: 'ri-water-flash-line',
    values: [
      { name: 'ナトリウム（Na）', normalRange: '138～145', unit: 'mEq/L', description: '体液バランスの指標', gender: 'both' },
      { name: 'カリウム（K）', normalRange: '3.6～4.8', unit: 'mEq/L', description: '心機能に重要な電解質', gender: 'both' },
      { name: 'クロール（Cl）', normalRange: '101～108', unit: 'mEq/L', description: '酸塩基平衡の指標', gender: 'both' },
      { name: 'カルシウム（Ca）', normalRange: '8.8～10.1', unit: 'mg/dL', description: '骨代謝・神経筋機能の指標', gender: 'both' },
    ]
  },
  {
    id: 'inflammation',
    name: '炎症マーカー',
    icon: 'ri-fire-line',
    values: [
      { name: 'CRP', normalRange: '0.00～0.14', unit: 'mg/dL', description: '炎症・感染症の指標', gender: 'both' },
      { name: '赤血球沈降速度（ESR）', normalRange: '男性：2～10 / 女性：3～15', unit: 'mm/h', description: '炎症の程度を反映', gender: 'both' },
      { name: '白血球分画（好中球）', normalRange: '40～71', unit: '%', description: '細菌感染の指標', gender: 'both' },
      { name: '白血球分画（リンパ球）', normalRange: '20～50', unit: '%', description: 'ウイルス感染の指標', gender: 'both' },
    ]
  },
  {
    id: 'thyroid',
    name: '甲状腺',
    icon: 'ri-capsule-line',
    values: [
      { name: 'TSH', normalRange: '0.61～4.23', unit: 'μIU/mL', description: '甲状腺機能の調節ホルモン', gender: 'both' },
      { name: 'FT3', normalRange: '2.30～4.00', unit: 'pg/mL', description: '活性型甲状腺ホルモン', gender: 'both' },
      { name: 'FT4', normalRange: '0.90～1.70', unit: 'ng/dL', description: '甲状腺ホルモンの主要形態', gender: 'both' },
    ]
  },
  {
    id: 'coagulation',
    name: '凝固系',
    icon: 'ri-time-line',
    values: [
      { name: 'PT（プロトロンビン時間）', normalRange: '10.0～13.0', unit: '秒', description: '外因系凝固能の評価', gender: 'both' },
      { name: 'PT-INR', normalRange: '0.85～1.15', unit: '', description: 'ワーファリン管理の指標', gender: 'both' },
      { name: 'APTT', normalRange: '25.0～40.0', unit: '秒', description: '内因系凝固能の評価', gender: 'both' },
      { name: 'フィブリノゲン', normalRange: '200～400', unit: 'mg/dL', description: '凝固因子の一つ', gender: 'both' },
      { name: 'Dダイマー', normalRange: '1.0未満', unit: 'μg/mL', description: '血栓形成の指標', gender: 'both' },
    ]
  }
];

export default function LabValuesPage() {
  const [activeTab, setActiveTab] = useState('hematology');
  const [searchQuery, setSearchQuery] = useState('');

  const activeCategory = labCategories.find(cat => cat.id === activeTab);

  const filteredValues = activeCategory?.values.filter(value =>
    value.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    value.description.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-cyan-50/40">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-teal-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform">
                <i className="ri-hospital-line text-white text-xl"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                  薬局マネジメント
                </h1>
                <p className="text-xs text-gray-500">Management Framework</p>
              </div>
            </Link>
            <Link
              to="/"
              className="flex items-center space-x-2 px-4 py-2 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-lg transition-colors"
            >
              <i className="ri-home-line"></i>
              <span className="text-sm font-medium">ホームに戻る</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl mb-4">
            <i className="ri-file-list-3-line text-white text-3xl"></i>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            検査値基準値一覧
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            薬剤師・医療従事者向けの主要検査項目の基準値をカテゴリ別に整理しました。
            日常業務での確認や患者説明にご活用ください。
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="max-w-2xl mx-auto relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
            <input
              type="text"
              placeholder="検査項目名で検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex space-x-2 min-w-max pb-2">
            {labCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                className={`flex items-center space-x-2 px-5 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                  activeTab === category.id
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-lg shadow-teal-500/30'
                    : 'bg-white text-gray-600 hover:bg-teal-50 hover:text-teal-700'
                }`}
              >
                <i className={`${category.icon} text-lg`}></i>
                <span className="text-sm">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Values Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
          {filteredValues.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-teal-50 to-cyan-50">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      検査項目
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      基準値
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      単位
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                      説明
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredValues.map((value, index) => (
                    <tr
                      key={index}
                      className="hover:bg-teal-50/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                          <span className="font-medium text-gray-900 text-sm">
                            {value.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-700 font-medium">
                          {value.normalRange}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {value.unit}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {value.description}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-16 text-center">
              <i className="ri-search-line text-gray-300 text-5xl mb-4"></i>
              <p className="text-gray-500 text-sm">
                検索条件に一致する項目が見つかりませんでした
              </p>
            </div>
          )}
        </div>

        {/* Notes Section */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <i className="ri-information-line text-amber-600 text-xl mt-0.5"></i>
            <div>
              <h3 className="font-semibold text-amber-900 mb-2 text-sm">
                ご利用上の注意
              </h3>
              <ul className="space-y-1 text-sm text-amber-800">
                <li>• 基準値は施設や測定方法により異なる場合があります</li>
                <li>• 年齢・性別・妊娠の有無などにより基準値が変動することがあります</li>
                <li>• 臨床判断は総合的な評価が必要です。必ず医師の診断を優先してください</li>
                <li>• 本情報は参考値であり、診断・治療の根拠とはなりません</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/drug-price-search"
            className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all"
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                <i className="ri-money-dollar-circle-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">薬価検索</h3>
            </div>
            <p className="text-xs text-gray-600">
              医薬品の薬価を素早く検索できます
            </p>
          </Link>

          <Link
            to="/package-insert-search"
            className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all"
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                <i className="ri-file-text-line text-cyan-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">添付文書検索</h3>
            </div>
            <p className="text-xs text-gray-600">
              医薬品の添付文書を確認できます
            </p>
          </Link>

          <Link
            to="/"
            className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all"
          >
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                <i className="ri-calculator-line text-purple-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">薬学計算ツール</h3>
            </div>
            <p className="text-xs text-gray-600">
              CCr、eGFR、BMIなどを計算できます
            </p>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-teal-600 to-cyan-700 text-white mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-semibold mb-4 text-sm">サービス</h3>
              <ul className="space-y-2 text-xs text-teal-100">
                <li><Link to="/services" className="hover:text-white transition-colors">サービス一覧</Link></li>
                <li><Link to="/family-pharmacist" className="hover:text-white transition-colors">かかりつけ薬剤師</Link></li>
                <li><Link to="/online-medication-guidance" className="hover:text-white transition-colors">オンライン服薬指導</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-sm">健康サポート</h3>
              <ul className="space-y-2 text-xs text-teal-100">
                <li><Link to="/health-support" className="hover:text-white transition-colors">健康サポート</Link></li>
                <li><Link to="/mental-health/stress-assessment" className="hover:text-white transition-colors">メンタルヘルス</Link></li>
                <li><Link to="/sarcopenia/muscle-strength-assessment" className="hover:text-white transition-colors">サルコペニア対策</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-sm">情報・リソース</h3>
              <ul className="space-y-2 text-xs text-teal-100">
                <li><Link to="/beast-blog" className="hover:text-white transition-colors">薬剤師ブログ</Link></li>
                <li><Link to="/theory" className="hover:text-white transition-colors">理論・フレームワーク</Link></li>
                <li><Link to="/pharmacist-resources" className="hover:text-white transition-colors">薬剤師リソース</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-sm">企業情報</h3>
              <ul className="space-y-2 text-xs text-teal-100">
                <li><Link to="/contact" className="hover:text-white transition-colors">お問い合わせ</Link></li>
                <li><Link to="/career" className="hover:text-white transition-colors">採用情報</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">プライバシーポリシー</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-teal-500 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-teal-100 mb-4 md:mb-0">
              © 2025 薬局マネジメントフレームワーク. All rights reserved.
            </p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-teal-100 hover:text-white transition-colors"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
